import { Box, Collapse, IconButton, Paper, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { OutlinedButton } from 'components/OutlinedButton';
import { formatMoney } from 'utils/helpers';
import { InvolvedPartyTable } from 'views/orders/charges/components/GeneralChargesTable/components/ChargesTableRow/components/PartiesInvolvedRow/components/InvolvedPartyTable';

import InfoComponent from '../info-component';
import { columnsDataSet } from './services';

const isPersistedEntry = (row: columnsDataSet) => (row.chargeData?._id?.length ?? 0) > 10;

const formatNumber = (row: columnsDataSet, type: string) => {
  if (type === 'addendums') return 'AD';

  const number = Number(row.number);
  return number < 10 ? `0${number}` : `${number}`;
};

type Props = {
  type: 'adjustments' | 'dues' | 'addendums';
  title: string;
  data: columnsDataSet[];
  handleEditModal: (item: columnsDataSet) => void;
  handleDelete: (item: columnsDataSet) => void;
  handleChangePayees?: (item: columnsDataSet) => void;
  handleChangePayers?: (item: columnsDataSet) => void;
  itemsCollapsed?: { [key: string]: boolean };
  onToggleCollapse?: (item: columnsDataSet) => void;
};

const useStyles = makeStyles((theme: any) => ({
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 500,
    color: theme.palette.tab.offselected,
  },
  card: {
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  numberBadge: {
    minWidth: 36,
    height: 36,
    padding: `0 ${theme.spacing(0.5)}`,
    borderRadius: theme.spacing(0.5),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontWeight: 600,
    fontSize: theme.typography.caption.fontSize,
  },
  description: {
    flex: 1,
    minWidth: 0,
    color: theme.palette.primary.dark,
    fontWeight: 500,
    wordBreak: 'break-word',
  },
  amount: {
    color: theme.palette.primary.dark,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  },
  parties: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  empty: {
    padding: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  addRow: {
    marginTop: theme.spacing(1),
  },
}));

const MobileKduesList: FC<Props> = ({
  type,
  title,
  data,
  handleEditModal,
  handleDelete,
  handleChangePayees,
  handleChangePayers,
  itemsCollapsed,
  onToggleCollapse,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const rows = data.filter(isPersistedEntry);

  const handleAddEntry = () => {
    const emptySlot = data.find((row) => !isPersistedEntry(row));
    if (emptySlot) {
      handleEditModal(emptySlot);
      return;
    }

    const base = data[0];
    if (!base) return;

    const nextNumber = Math.max(...data.map((row) => Number(row.number) || 0)) + 1;
    handleEditModal({
      id: `new-${nextNumber}`,
      indexRow: 0,
      number: nextNumber,
      description: '',
      chargeData: {
        ...base.chargeData,
        _id: '',
        number: nextNumber,
        description: '',
        amount: 0,
      },
    });
  };

  return (
    <Box>
      <Typography variant="subtitle2" className={classes.sectionTitle}>
        {title}
      </Typography>

      {rows.length === 0 && (
        <Typography variant="body2" className={classes.empty}>
          {t('charges:noEntries', { defaultValue: 'No entries yet' })}
        </Typography>
      )}

      {rows.map((row) => {
        const expanded = Boolean(itemsCollapsed?.[row.id]);
        const parties = row.chargeData?.involved_parties ?? [];
        const payees = parties.filter((party) => party.kind.includes('Payee'));
        const payers = parties.filter((party) => party.kind.includes('Payer'));
        const canDelete =
          row.chargeData.kind.includes('manual') && isPersistedEntry(row);

        return (
          <Paper key={row.id} variant="outlined" className={classes.card}>
            <Box className={classes.cardHeader}>
              <Box className={classes.numberBadge}>{formatNumber(row, type)}</Box>

              <Typography variant="body2" className={classes.description}>
                {row.description || '—'}
              </Typography>

              <Typography variant="body2" className={classes.amount}>
                {formatMoney(row.chargeData?.amount || 0)}
              </Typography>

              {onToggleCollapse && (
                <IconButton
                  aria-label={t('charges:toggleParties', { defaultValue: 'Show parties' })}
                  aria-expanded={expanded}
                  onClick={() => onToggleCollapse(row)}
                  size="large"
                >
                  <FontAwesomeIcon icon={expanded ? 'chevron-up' : 'chevron-down'} size="sm" />
                </IconButton>
              )}

              <InfoComponent
                onClickEdit={() => handleEditModal(row)}
                onChangePayees={handleChangePayees ? () => handleChangePayees(row) : undefined}
                onChangePayers={handleChangePayers ? () => handleChangePayers(row) : undefined}
                onClickDelete={canDelete ? () => handleDelete(row) : undefined}
              />
            </Box>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box className={classes.parties}>
                {payees.length > 0 && (
                  <InvolvedPartyTable
                    parties={payees}
                    title={t('orders:payeesLabel')}
                    onEditClick={() => handleChangePayees?.(row)}
                  />
                )}
                {payers.length > 0 && (
                  <InvolvedPartyTable
                    parties={payers}
                    title={t('orders:payersLabel')}
                    onEditClick={() => handleChangePayers?.(row)}
                  />
                )}
                {payees.length === 0 && payers.length === 0 && (
                  <Typography variant="body2" className={classes.empty}>
                    {t('charges:noParties', { defaultValue: 'No parties assigned yet' })}
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Paper>
        );
      })}

      {type !== 'addendums' && (
        <Box className={classes.addRow}>
          <OutlinedButton
            text={t('charges:add-entry')}
            icon="plus"
            size="small"
            onClick={handleAddEntry}
          />
        </Box>
      )}
    </Box>
  );
};

export default MobileKduesList;
